import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HardDrive, AlertTriangle, CheckCircle } from 'lucide-react';
import { appConfig, formatBytes, getStoragePercentage } from '../../config/app.config';

export function StorageIndicator({ userInfo, className = "" }) {
  const [storageData, setStorageData] = useState({
    storageUsed: userInfo?.storageUsed || 0,
    storageLimit: appConfig.limits.storageLimit,
    usagePercentage: 0
  });
  const [loading, setLoading] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);

  const fetchStorageUsage = useCallback(async () => {
    if (!userInfo?.userId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${appConfig.apiUrl}/api/alpha/storage-usage/${userInfo.userId}`);
      if (response.ok) {
        const data = await response.json();
        setStorageData({
          storageUsed: data.storageUsed,
          storageLimit: data.storageLimit,
          usagePercentage: data.usagePercentage
        });
      }
    } catch (error) {
      console.error('Failed to fetch storage usage:', error);
    } finally {
      setLoading(false);
    }
  }, [userInfo?.userId]);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchStorageUsage();
    }
  }, [fetchStorageUsage, userInfo?.userId]);

  // Only render for alpha users
  if (!appConfig.limits.storageLimit) return null;

  const { storageUsed, storageLimit, usagePercentage } = storageData;
  const remainingStorage = storageLimit - storageUsed;
  
  // Determine status and colors
  const getStatusInfo = () => {
    if (usagePercentage >= 95) {
      return {
        status: 'critical',
        color: 'text-red-400',
        bgColor: 'bg-red-500',
        icon: AlertTriangle,
        message: 'Storage almost full!'
      };
    } else if (usagePercentage >= 80) {
      return {
        status: 'warning',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-500',
        icon: AlertTriangle,
        message: 'Storage getting full'
      };
    } else {
      return {
        status: 'good',
        color: 'text-green-400',
        bgColor: 'bg-green-500',
        icon: CheckCircle,
        message: 'Storage healthy'
      };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <motion.div
      className={`bg-[#2a282a] rounded-lg p-4 border border-[#494646] ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
    >
      {/* <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-300">Storage Usage</span>
        </div>
        <div className="flex items-center gap-1">
          <statusInfo.icon className={`h-3 w-3 ${statusInfo.color}`} />
          <span className={`text-xs ${statusInfo.color}`}>
            {statusInfo.message}
          </span>
        </div>
      </div> */}

      {/* Progress Bar */}
      <div className="mt-4 mb-1">
        <div className="w-full bg-[#151415] rounded-full h-2 overflow-hidden">
          <motion.div
            className={`h-full ${statusInfo.bgColor} rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(usagePercentage, 100)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Usage Stats */}
      <div className="flex items-center justify-between text-xs">
        <div className="text-gray-400">
          <span className="text-gray-200 font-medium">
            {formatBytes(storageUsed)}
          </span>
          {' '}of {formatBytes(storageLimit)} used
        </div>
        <div className="flex items-center gap-2">
          {/* <div className={`font-medium ${statusInfo.color}`}>
            {usagePercentage.toFixed(1)}%
          </div> */}
          {/* Refresh Button */}
          {!loading && (
            <motion.button
              onClick={fetchStorageUsage}
              className="text-xs text-purple-400 hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-500/10 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Refresh Usage"
            >
              ↻
            </motion.button>
          )}
          {loading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-500"></div>
            </div>
          )}
        </div>
      </div>

      {/* Remaining Storage */}
      {/* <div className="mt-2 text-xs text-gray-500">
        {formatBytes(remainingStorage)} remaining
      </div> */}

      {/* Fixed height container for warning to prevent layout shift */}
      <div className="mt-3" style={{ minHeight: usagePercentage >= 90 && !warningDismissed ? '60px' : '0px' }}>
        {/* Warning Message */}
        {usagePercentage >= 90 && !warningDismissed && (
          <motion.div
            className="p-2 bg-red-500/10 border border-red-500/30 rounded-md"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-red-300 flex-1">
                <p className="font-medium">Storage limit approaching</p>
                <p className="mt-1">
                  {usagePercentage >= 95 
                    ? "You're almost out of space. Consider deleting unused files."
                    : "You're using most of your storage. New uploads may be blocked soon."
                  }
                </p>
              </div>
              <motion.button
                onClick={() => setWarningDismissed(true)}
                className="text-red-400 hover:text-red-300 ml-1 flex-shrink-0"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Dismiss warning"
              >
                ×
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}